import React, { Component } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { ButtonMedium, media } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { CaptionText, SmallText, Text, TinyText } from 'app/common/typography'
import type { FilteredRestaurant, RestaurantShortDTO } from 'shared/types'
import onClickOutside from 'react-onclickoutside'
import { ModalTitle } from 'app/common/modals/title'
import { EmptyCircle, RadioChecked, Spinner } from 'app/assets/icons'
import { InputAddressAutocomplete } from 'app/store-locator/input-address-autocomplete'

const { grey, grey30, orange2, red, text, yellow, white } = colors

type Props = {
    desktop?: boolean,
    loading?: boolean,
    fromKingdom?: boolean,
    fromBirthday?: boolean,
    handleClose: () => void,
    restaurants: Array<FilteredRestaurant>,
    addRestaurant: RestaurantShortDTO => void,
    selectRestaurant: RestaurantShortDTO => void,
    selectedRestaurant: RestaurantShortDTO,
    resetAddRestaurantSuccess?: () => void,
    addRestaurantSuccess?: boolean,
    addRestaurantError?: string,
    onClick?: (selectedRestaurant: RestaurantShortDTO) => void
}

type State = {
    isSubmitting: boolean,
    width: number
}

class _Content extends Component<Props, State> {
    state = {
        isSubmitting: false,
        width: window.innerWidth
    }

    componentDidMount() {
        window.addEventListener('resize', this.updateWindowDimensions)
    }

    componentDidUpdate(prevProps: Props) {
        const {
            handleClose,
            addRestaurantSuccess,
            addRestaurantError
        } = this.props
        const { isSubmitting } = this.state
        if (
            addRestaurantSuccess &&
            addRestaurantSuccess !== prevProps.addRestaurantSuccess
        ) {
            this.setState({ isSubmitting: false })
            handleClose()
            return
        }
        if (isSubmitting && addRestaurantError) {
            this.setState({ isSubmitting: false })
        }
    }

    componentWillUnmount() {
        const { resetAddRestaurantSuccess } = this.props
        resetAddRestaurantSuccess && resetAddRestaurantSuccess()
    }

    updateWindowDimensions = () => this.setState({ width: window.innerWidth })

    submit = () => {
        const {
            selectedRestaurant,
            onClick,
            addRestaurant,
            handleClose,
            fromBirthday
        } = this.props
        if (!selectedRestaurant) {
            return
        }
        this.setState({ isSubmitting: true })
        if (onClick) {
            onClick(selectedRestaurant)
            handleClose()
        } else {
            addRestaurant(selectedRestaurant)
        }
        if (fromBirthday) {
            handleClose()
        }
    }

    handleSelectRestaurant = (r: FilteredRestaurant) => {
        const { fromKingdom, fromBirthday, selectRestaurant } = this.props
        if ((fromKingdom && !r.kingdom) || (fromBirthday && !r.birthday)) {
            return
        }
        selectRestaurant(r)
    }

    render() {
        const {
            fromKingdom,
            fromBirthday,
            desktop,
            loading,
            restaurants,
            handleClose,
            addRestaurantError,
            selectedRestaurant
        } = this.props
        const { isSubmitting } = this.state
        const selectedRestaurantId = selectedRestaurant && selectedRestaurant.id
        const nbRestaurants = restaurants.length
        return (
            <Main>
                <HeaderContainer>
                    <ModalTitle center handleClose={handleClose} />
                    <AddressSearchContainer>
                        <InputAddressAutocomplete />
                    </AddressSearchContainer>
                    <ResultsTitleContainer>
                        {loading && desktop ? (
                            <Spinner size={30} />
                        ) : (
                            <ResultsTitle color={grey}>
                                <FormattedMessage
                                    values={{
                                        number: nbRestaurants
                                    }}
                                    id="storelocator.results.title.search"
                                />
                            </ResultsTitle>
                        )}
                    </ResultsTitleContainer>
                </HeaderContainer>
                <RestaurantsContainer>
                    {restaurants.map((r: FilteredRestaurant) => (
                        <RadioContainer
                            key={r.id}
                            onClick={() => this.handleSelectRestaurant(r)}>
                            <Label>
                                <Radio
                                    type="radio"
                                    name="restaurant"
                                    defaultChecked={
                                        r.id === selectedRestaurantId
                                    }
                                    value={r.id}
                                />
                                <IconContainer>
                                    {(r.kingdom && fromKingdom) ||
                                    (r.birthday && fromBirthday) ? (
                                        r.id === selectedRestaurantId ? (
                                            <RadioChecked />
                                        ) : (
                                            <EmptyCircle color={text} />
                                        )
                                    ) : (
                                        <InactiveRadio />
                                    )}
                                </IconContainer>
                                <RestaurantInfos
                                    active={
                                        (r.kingdom && fromKingdom) ||
                                        (r.birthday && fromBirthday)
                                    }>
                                    <Text>{r.name}</Text>
                                    <CaptionText>{r.addressFull}</CaptionText>
                                    {((fromKingdom && !r.kingdom) ||
                                        (fromBirthday && !r.birthday)) && (
                                        <FormattedMessage
                                            id={`form.restaurant.not.${
                                                fromKingdom
                                                    ? `kingdom`
                                                    : `birthday`
                                            }`}>
                                            {(text: string) => (
                                                <Warning>{text}</Warning>
                                            )}
                                        </FormattedMessage>
                                    )}
                                </RestaurantInfos>
                            </Label>
                        </RadioContainer>
                    ))}
                </RestaurantsContainer>
                <FooterContainer>
                    <ButtonContainer>
                        <StyledButtonMedium
                            onClick={handleClose}
                            background="none"
                            color={red}
                            border={red}
                            disabled={isSubmitting}>
                            <FormattedMessage id="form.button.cancel" />
                        </StyledButtonMedium>
                        <StyledButtonMedium
                            onClick={this.submit}
                            type="button"
                            background={yellow}
                            border={yellow}
                            disabled={isSubmitting || !selectedRestaurant}>
                            <FormattedMessage id="kingdom.profile.form.button.add" />
                        </StyledButtonMedium>
                    </ButtonContainer>
                    {addRestaurantError && (
                        <Mentions>
                            <FormattedMessage id={addRestaurantError}>
                                {(text: string) => (
                                    <Text color={red}>{text}</Text>
                                )}
                            </FormattedMessage>
                        </Mentions>
                    )}
                </FooterContainer>
            </Main>
        )
    }
}

const Main = styled.div`
    padding: 0 20px;
    height: 100dvh;
    background: ${white};
    display: flex;
    flex-direction: column;
    ${media.medium`
        padding: 40px;
        height: 500px;
    `};
`

const HeaderContainer = styled.div`
    flex-shrink: 0;
`

const AddressSearchContainer = styled.div`
    margin-top: 20px;
    margin-bottom: 20px;
    ${media.medium`
        margin-top: 0;
    `};
`

const ResultsTitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 20px 0 10px;
    border-bottom: 1px solid ${grey30};
`

const ResultsTitle = styled(SmallText)`
    text-transform: uppercase;
`

const RestaurantsContainer = styled.div`
    flex: 1;
    overflow: auto;
    min-height: 0;
`

const FooterContainer = styled.div`
    flex-shrink: 0;
`

const RadioContainer = styled.div`
    padding: 10px 0;
    cursor: pointer;
`

const Radio = styled.input`
    -webkit-appearance: none;
    outline: 0;
    display: none;
`

const Label = styled.label`
    display: inline-flex;
    cursor: pointer;
    align-items: center;
`

const IconContainer = styled.span`
    margin-right: 5px;
`

const InactiveRadio = styled(EmptyCircle)`
    opacity: 0.5;
`
const Warning = styled(TinyText)`
    color: ${orange2}
    margin: 0.3em 0;
`

const RestaurantInfos = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    padding-left: 10px;
    opacity: ${props => (!props.active ? 0.5 : 1)};
`

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    margin: 20px auto;
    justify-content: space-between;
    ${media.medium`
        justify-content: center;
    `};
`

const StyledButtonMedium = styled(ButtonMedium)`
    align-self: center;
    width: calc(50% - 10px);
    ${media.medium`
        width: calc(50% - 20px);
        max-width: 200px;
        margin: 0 10px;
    `};
`

const Mentions = styled.div`
    margin-top: 25px;
    margin-bottom: 25px;
`

const Content = onClickOutside(_Content)

export { Content }
